/**
 * Quick Presets
 *
 * Horizontal button group for quick date range selection.
 * Horizontally scrollable on mobile.
 */

import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import { PopoverTrigger } from "@/components/ui/popover";
import { QUICK_PRESETS, type QuickPreset, type DateRangeValue } from "./types";
import { isCustomRange } from "./types";
import { getDisplayLabel } from "./utils";

type QuickPresetsProps = {
  /** Current selected value */
  value: DateRangeValue;
  /** Callback when a preset is clicked */
  onPresetClick: (preset: QuickPreset) => void;
  /** Callback when Custom button is clicked */
  onCustomClick: () => void;
  /** Whether the custom popover is open */
  isCustomOpen: boolean;
  /** Optional className */
  className?: string;
};

export function QuickPresets({
  value,
  onPresetClick,
  onCustomClick,
  isCustomOpen,
  className,
}: QuickPresetsProps) {
  // Check if current value is a quick preset
  const isQuickPreset = typeof value === "string" && 
    QUICK_PRESETS.some(p => p.value === value as string);
  
  // Check if current value is custom or an extended preset
  const isCustomOrExtended = isCustomRange(value) || 
    (typeof value === "string" && !isQuickPreset);

  // Get custom display label if custom range is selected
  const customLabel = isCustomRange(value) 
    ? getDisplayLabel(value)
    : isCustomOrExtended && typeof value === "string"
      ? value.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
      : "Custom";

  return (
    <div
      className={cn(
        "flex items-center gap-1 p-1 bg-muted/50 rounded-lg",
        "overflow-x-auto scrollbar-none",
        className
      )}
    >
      {/* Quick preset buttons */}
      {QUICK_PRESETS.map((preset) => (
        <button
          key={preset.value}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPresetClick(preset.value);
          }}
          className={cn(
            "px-2.5 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap flex-shrink-0",
            "lg:px-4 lg:py-1.5 lg:text-sm",
            value === preset.value
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          {preset.label}
        </button>
      ))}

      {/* Custom button with dropdown indicator - wrapped in PopoverTrigger */}
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={onCustomClick}
          className={cn(
            "px-2.5 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap flex-shrink-0",
            "lg:px-4 lg:py-1.5 lg:text-sm",
            "flex items-center gap-1",
            isCustomOrExtended
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          <span className="max-w-[120px] truncate">
            {isCustomOrExtended ? customLabel : "Custom"}
          </span>
          <ChevronDownIcon 
            className={cn(
              "h-3 w-3 lg:h-3.5 lg:w-3.5 transition-transform flex-shrink-0",
              isCustomOpen && "rotate-180"
            )} 
          />
        </button>
      </PopoverTrigger>
    </div>
  );
}
